import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ClipboardList, Boxes, PartyPopper } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LangContext";

export default function GlobalSearch() {
  const { t } = useLang();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const box = useRef();

  useEffect(() => {
    if (!q.trim()) { setRes(null); return; }
    const id = setTimeout(() => api.get("/search", { params: { q } }).then((r) => { setRes(r.data); setOpen(true); }).catch(() => {}), 250);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => {
    const onDoc = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const go = (path) => { setOpen(false); setQ(""); nav(path); };
  const total = (res?.orders?.length || 0) + (res?.equipment?.length || 0) + (res?.events?.length || 0);

  return (
    <div ref={box} className="relative w-full max-w-md" data-testid="global-search">
      <div className="flex items-center gap-2 px-3 h-9 rounded-lg bg-white/5 border border-white/10 focus-within:border-indigo-400/50">
        <Search className="w-4 h-4 text-slate-400" />
        <input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => res && setOpen(true)}
          placeholder={t("search_global")}
          className="bg-transparent outline-none text-sm w-full placeholder:text-slate-500"
        />
      </div>
      {open && res && (
        <div data-testid="global-search-results" className="absolute left-0 right-0 mt-2 rounded-xl bg-slate-900 border border-white/10 shadow-2xl overflow-hidden z-50">
          {total === 0 && <div className="px-4 py-3 text-sm text-slate-500">{t("no_data")}</div>}
          {res.orders.length > 0 && (
            <div>
              <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest text-slate-500">{t("orders")}</div>
              {res.orders.map((o) => (
                <button key={o.id} data-testid={`search-order-${o.id}`} onClick={() => go(`/orders?open=${o.id}`)} className="w-full text-left px-4 py-2 hover:bg-white/5 flex items-center gap-3">
                  <ClipboardList className="w-4 h-4 text-indigo-300" />
                  <span className="text-sm flex-1 truncate">{o.title}</span>
                  <span className="text-[10px] text-slate-400">{t(o.type)} · {t(o.status)}</span>
                </button>
              ))}
            </div>
          )}
          {res.equipment.length > 0 && (
            <div className="border-t border-white/5">
              <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest text-slate-500">{t("equipment")}</div>
              {res.equipment.map((e) => (
                <button key={e.id} data-testid={`search-equipment-${e.id}`} onClick={() => go(`/equipment?open=${e.id}`)} className="w-full text-left px-4 py-2 hover:bg-white/5 flex items-center gap-3">
                  <Boxes className="w-4 h-4 text-emerald-300" />
                  <span className="text-sm flex-1 truncate">{e.name}</span>
                  <span className="text-[10px] text-slate-400">{e.category} · {e.available}/{e.quantity}</span>
                </button>
              ))}
            </div>
          )}
          {res.events?.length > 0 && (
            <div className="border-t border-white/5">
              <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest text-slate-500">{t("events")}</div>
              {res.events.map((e) => (
                <button key={e.id} data-testid={`search-event-${e.id}`} onClick={() => go(`/events?open=${e.id}`)} className="w-full text-left px-4 py-2 hover:bg-white/5 flex items-center gap-3">
                  <PartyPopper className="w-4 h-4 text-fuchsia-300" />
                  <span className="text-sm flex-1 truncate">{e.name}</span>
                  <span className="text-[10px] text-slate-400">{e.start_date}{e.location ? ` · ${e.location}` : ""}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
